/**
 * CockpitService — API client for the Cockpit AI agent backend.
 *
 * Handles session CRUD and SSE-streamed chat responses.
 * All methods require a valid Keycloak JWT token.
 */

import { readSSE } from '@digitalisation/enlixy-comp/assistant';
import { DEMO_HEADER, isDemoMode } from './demoMode';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CockpitSession {
  id: string;
  user_id: string;
  org_id: string;
  agent_type: string;
  title: string;
  message_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CockpitMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export interface SSEEvent {
  type:
    | 'text_delta'
    | 'reasoning_delta'
    | 'tool_call'
    | 'tool_result'
    | 'block_start'
    | 'block_stop'
    | 'message_stop'
    | 'metadata'
    | 'citations'
    | 'error';
  text?: string;
  block_type?: string;
  session_id?: string;
  error?: string;
  /** Full response text (may appear in message_stop) */
  response?: string;
  /** Tool names used (may appear in message_stop — ignored by frontend) */
  toolsUsed?: string[];
  usage?: { input_tokens: number; output_tokens: number };
  /** Documentation citations (type 'citations'): see components/cockpit/citations.ts */
  citations?: unknown[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getBaseUrl(): string {
  // In development, use empty string so requests go through the Vite dev proxy
  if (import.meta.env.DEV) return '';
  return import.meta.env.VITE_COCKPIT_API_URL || 'https://chatapi.enlixy-test.com';
}

/**
 * Headers for every cockpit API call — the single construction site, so the
 * demo header cannot be missed on one endpoint.
 *
 * Note for deployment: a custom header makes even the GETs preflight, so the
 * API must list `X-Enlixy-Demo` in `Access-Control-Allow-Headers`. Development
 * goes through the same-origin Vite proxy and won't surface a missing entry.
 */
function authHeaders(token: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  if (isDemoMode()) {
    headers[DEMO_HEADER] = 'true';
  }
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Cockpit API error ${response.status}: ${body || response.statusText}`);
  }
  return response.json();
}

// ─── Session API ─────────────────────────────────────────────────────────────

export async function createSession(
  token: string,
  title?: string,
): Promise<CockpitSession> {
  const response = await fetch(`${getBaseUrl()}/api/sessions`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ use_case: 'cockpit', title }),
  });
  return handleResponse<CockpitSession>(response);
}

export async function listSessions(token: string): Promise<CockpitSession[]> {
  const response = await fetch(`${getBaseUrl()}/api/sessions?agent_type=cockpit`, {
    headers: authHeaders(token),
  });
  return handleResponse<CockpitSession[]>(response);
}

export async function getSession(
  token: string,
  sessionId: string,
): Promise<CockpitSession> {
  const response = await fetch(`${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}`, {
    headers: authHeaders(token),
  });
  return handleResponse<CockpitSession>(response);
}

export async function getMessages(
  token: string,
  sessionId: string,
): Promise<CockpitMessage[]> {
  const response = await fetch(
    `${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}/messages`,
    { headers: authHeaders(token) },
  );
  return handleResponse<CockpitMessage[]>(response);
}

export async function renameSession(
  token: string,
  sessionId: string,
  title: string,
): Promise<void> {
  const response = await fetch(
    `${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'PUT',
      headers: authHeaders(token),
      body: JSON.stringify({ title }),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Cockpit API error ${response.status}: ${body || response.statusText}`);
  }
}

export async function deleteSession(
  token: string,
  sessionId: string,
): Promise<void> {
  const response = await fetch(
    `${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Cockpit API error ${response.status}: ${body || response.statusText}`);
  }
}

// ─── SSE Chat Stream ─────────────────────────────────────────────────────────

/**
 * Send a chat message and stream SSE events.
 *
 * Yields parsed SSEEvent objects. The caller can consume these
 * to build the assistant's response incrementally.
 *
 * The AbortSignal allows cancellation from the UI.
 *
 * ## `instructions` — read before using
 *
 * Directives (formatting, house style, output shape) belong here, NOT prepended
 * to `message`. The backend folds `instructions` into the system prompt for the
 * call, and only `message` is stored in session history — so splitting them also
 * keeps history and auto-generated titles showing the actual question instead of
 * boilerplate.
 *
 * It also **bypasses the guardrail's input scan**, which is why a long directive
 * block in `message` gets rejected as a prompt attack. That bypass is the
 * constraint: `instructions` must only ever carry client-side constants, or text
 * assembled from a fixed set of UI options. Never route anything the user typed
 * through it, or you've built a clean path around PROMPT_ATTACK.
 */
export async function* streamChat(
  token: string,
  sessionId: string,
  message: string,
  abortSignal?: AbortSignal,
  instructions?: string,
): AsyncGenerator<SSEEvent> {
  const response = await fetch(
    `${getBaseUrl()}/api/sessions/${encodeURIComponent(sessionId)}/chat`,
    {
      method: 'POST',
      headers: authHeaders(token),
      // `instructions` is folded into the system prompt for this call and is not
      // persisted, so it must be sent on every request — omitting it on a
      // follow-up silently drops the directives.
      body: JSON.stringify(
        instructions ? { message, instructions } : { message },
      ),
      signal: abortSignal,
    },
  );

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(`Cockpit chat error ${response.status}: ${body || response.statusText}`);
  }

  // The shared SSE reader: same parsing as before (`data: {json}` lines,
  // `data: [DONE]` ends the stream, malformed events are skipped).
  for await (const event of readSSE<SSEEvent>(response)) {
    console.debug('🤖 SSE event:', event.type, event);
    yield event;
  }
}
